import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getSignedMeetupVideoUrl } from "@/features/video/upload";
import { fetchParticipantById, type ParticipantRow } from "@/lib/api/participants";
import { getParticipantSession } from "@/lib/participant-session";

import { LinkButton } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { CirclePlayer } from "@/components/ui/CirclePlayer";
import { InlineAlert } from "@/components/ui/States";
import { ParticipantShell } from "@/components/layout/ParticipantShell";
import { ProfilePreviewCard } from "@/features/profile/ProfilePreviewCard";

interface ProfileJson {
  about?: string;
  looking_for?: string | string[];
  can_help_with?: string | string[];
}

/** AI-processing timeout — after this many ms without profile_json we treat
 *  the pitch as failed and surface the D-3a screen. */
const PROCESSING_TIMEOUT_MS = 90_000;

function normalizeList(value: string | string[] | undefined): string {
  if (!value) return "";
  if (Array.isArray(value)) return value.map((v) => `· ${v}`).join("\n");
  return value;
}

export function ProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const session = slug ? getParticipantSession(slug) : null;

  useEffect(() => {
    if (!slug) return;
    if (!session) navigate(`/e/${slug}`, { replace: true });
  }, [slug, session, navigate]);

  const { data: participant, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ["participant", session?.participantId],
    queryFn: () => fetchParticipantById(session?.participantId ?? ""),
    enabled: Boolean(session?.participantId),
    refetchInterval: (query) => (query.state.data?.profile_json ? false : 3000),
  });

  const { data: videoUrl } = useQuery({
    queryKey: ["pitch-video", session?.participantId, participant?.video_storage_path],
    queryFn: () =>
      getSignedMeetupVideoUrl({
        eventId: session?.eventId ?? "",
        participantId: session?.participantId ?? "",
        targetPath: participant?.video_storage_path ?? "",
      }),
    enabled: Boolean(
      session?.eventId && session?.participantId && participant?.video_storage_path,
    ),
    staleTime: 4 * 60 * 1000,
  });

  // Track how long we've been polling. If profile_json hasn't landed within
  // PROCESSING_TIMEOUT_MS, surface the D-3a "failed" screen.
  const [pollStartedAt] = useState(() => Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (participant?.profile_json) return;
    const t = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(t);
  }, [participant?.profile_json, dataUpdatedAt]);

  if (!session) return null;

  const profile = (participant?.profile_json as ProfileJson | null) ?? null;
  const status = participant?.pitch_status;
  const isFlagged = status === "flagged" || status === "submitted";
  const isRejected = status === "rejected";
  const hasVideo = Boolean(participant?.video_storage_path);
  // Distinguish "no video yet" from "video uploaded, AI working":
  //   - needsVideo  → participant exists but never uploaded a pitch.
  //     This happens if the recording / upload failed silently, or the user
  //     completed the registration form and bounced before recording.
  //     Before this branch existed, the page spun "Generating your profile…"
  //     forever for these users — there was nothing to generate from.
  //   - processingTimedOut → video is in Storage but AI didn't return a
  //     profile_json within PROCESSING_TIMEOUT_MS.
  //   - isProcessing → video uploaded, profile not yet, still within budget.
  const needsVideo =
    participant != null && !hasVideo && !isFlagged && !isRejected;
  const processingTimedOut =
    hasVideo && !profile && !isFlagged && !isRejected &&
    now - pollStartedAt > PROCESSING_TIMEOUT_MS;
  const isProcessing =
    hasVideo && !profile && !processingTimedOut && !isFlagged && !isRejected;

  return (
    <ParticipantShell>
      <div className="animate-fade-in">
        {needsVideo ? (
          <NoVideoYet slug={slug ?? ""} participantName={participant?.name ?? session.name} />
        ) : processingTimedOut ? (
          <ProcessingFailed slug={slug ?? ""} />
        ) : isFlagged ? (
          <FlaggedForReview
            participant={participant}
            session={session}
            slug={slug ?? ""}
            videoUrl={videoUrl}
          />
        ) : isRejected ? (
          <RejectedPitch slug={slug ?? ""} />
        ) : (
          <ProfileContent
            slug={slug ?? ""}
            isProcessing={isProcessing}
            isLoading={isLoading}
            participant={participant}
            profile={profile}
            session={session}
            videoUrl={videoUrl}
            onEdit={() => navigate(`/e/${slug ?? ""}/profile-settings`)}
            onContinue={() => navigate(`/e/${slug ?? ""}/feed`)}
          />
        )}
      </div>
    </ParticipantShell>
  );
}

/* ------------------------------------------------------------------ */
/* No video yet — user completed the form but never uploaded a pitch.   */
/* ------------------------------------------------------------------ */

function NoVideoYet({ slug, participantName }: { slug: string; participantName: string }) {
  return (
    <Card padding="md">
      <div className="text-center">
        <div className="text-5xl">🎥</div>
        <h2 className="mt-3 text-[20px] font-bold text-[#1a1a1a]">
          One step left, {participantName.split(" ")[0]}
        </h2>
        <p className="mt-1 text-[13px] text-[#6c757d]">
          You're registered for the event — now record a 30-60 second pitch so
          others know who you are and what you're looking for.
        </p>
      </div>

      <Card tone="info" padding="sm" className="mt-5 shadow-none">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#1d4ed8]">
          What to cover
        </p>
        <ul className="mt-1 space-y-1 text-[13px] text-[#1d4ed8]">
          <li>· Who you are and what you do</li>
          <li>· Your current focus or challenge</li>
          <li>· How you can help others — this surfaces you in others' feeds</li>
        </ul>
      </Card>

      <Card tone="approved" padding="sm" className="mt-3 shadow-none">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#065f46]">
          Until you record
        </p>
        <p className="mt-1 text-[13px] text-[#065f46]">
          You won't appear in the feed for other participants. Recording takes
          about a minute.
        </p>
      </Card>

      <div className="mt-5 flex flex-col gap-2">
        <LinkButton to={`/e/${slug}/pitch`} fullWidth>
          Record pitch now
        </LinkButton>
        <LinkButton to={`/e/${slug}/feed`} variant="secondary" fullWidth>
          Browse other participants first
        </LinkButton>
      </div>
    </Card>
  );
}

interface ProfileContentProps {
  slug: string;
  isProcessing: boolean;
  isLoading: boolean;
  participant: ParticipantRow | null | undefined;
  profile: ProfileJson | null;
  session: { name: string; email: string };
  videoUrl: string | undefined;
  onEdit: () => void;
  onContinue: () => void;
}

function ProfileContent({
  slug,
  isProcessing,
  isLoading,
  participant,
  profile,
  session,
  videoUrl,
  onEdit,
  onContinue,
}: ProfileContentProps) {
  // ── Processing-state branch — kept on the original simpler card so we don't
  // promise the polished preview before AI has actually produced any copy.
  if (isProcessing || (isLoading && !participant)) {
    return (
      <>
        <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">
          Creating your profile…
        </h1>
        <p className="mt-2 text-[14px] text-[#6c757d]">
          We're transcribing your video and writing your profile. Usually 30–60 seconds.
        </p>

        <Card padding="md" className="mt-5">
          <div className="text-center">
            {videoUrl ? (
              <CirclePlayer
                key={videoUrl}
                src={videoUrl}
                className="mx-auto h-40 w-40 border border-[#e2e8f0]"
              />
            ) : (
              <div className="mx-auto h-40 w-40 overflow-hidden rounded-full border border-[#e2e8f0] bg-gradient-to-br from-[#e2e8f0] to-[#cbd5e1]" />
            )}
            <h2 className="mt-3 text-[18px] font-bold text-[#1a1a1a]">
              {participant?.name ?? session.name}
            </h2>
            <p className="mt-0.5 text-[13px] text-[#6c757d]">
              {[participant?.role, participant?.company].filter(Boolean).join(" · ") ||
                session.email}
            </p>
          </div>

          {isLoading && !participant ? (
            <p className="mt-4 text-center text-[13px] text-[#6c757d]">Loading...</p>
          ) : (
            <div className="mt-5">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-[#e6f1fb] border-t-[#2563eb]" />
              <p className="mt-3 text-center text-[13px] text-[#6c757d]">
                Generating your profile from your pitch...
              </p>
              <InlineAlert tone="info" className="mt-4 text-left">
                <strong className="block">You can close the app.</strong>
                We'll email you when your profile is published, and again when matches are revealed 2 hours before the event.
              </InlineAlert>
              {participant?.looking_for ? (
                <p className="mt-4 text-[13px] text-[#475569]">
                  <span className="font-semibold text-[#2563eb]">Looking for:</span>{" "}
                  {participant.looking_for}
                </p>
              ) : null}
              {participant?.can_help_with ? (
                <p className="mt-2 text-[13px] text-[#475569]">
                  <span className="font-semibold text-[#2563eb]">Can help with:</span>{" "}
                  {participant.can_help_with}
                </p>
              ) : null}
            </div>
          )}
        </Card>
      </>
    );
  }

  // ── Live preview — the v6.05 "how others see you" card. profile_json has
  // landed; videoUrl may still be loading (the inner component renders the
  // gradient placeholder while the signed URL resolves).
  const subtitle =
    [participant?.role, participant?.company].filter(Boolean).join(" · ") ||
    session.email;

  return (
    <>
      <h1 className="text-[22px] font-bold tracking-[-0.4px] text-[#1a1a1a]">
        Here's how others will see you
      </h1>
      <p className="mt-1.5 text-[14px] leading-[1.5] text-[#6c757d]">
        You can edit any field — matches recompute in the background.
      </p>

      <div className="mt-6">
        <ProfilePreviewCard
          name={participant?.name ?? session.name}
          subtitle={subtitle}
          videoUrl={videoUrl}
          lookingFor={normalizeList(profile?.looking_for) || null}
          about={profile?.about ?? null}
          canHelpWith={normalizeList(profile?.can_help_with) || null}
          onEdit={onEdit}
          onContinue={onContinue}
        />
      </div>

      {/* TZ block B item 6: re-entry point to the Adizes read of the
          pitch. Only surfaced when the analysis has actually landed —
          otherwise the link is a dead end. */}
      {/* V2 spec: re-entry point to the qualitative read of the
          pitch. Surfaced when the new pitch_analysis has landed; we
          also fall back to the legacy pitch_paei flag so participants
          whose recordings predate the v2 reframe still see the link. */}
      {participant?.pitch_analysis || participant?.pitch_paei ? (
        <LinkButton
          to={`/e/${slug}/pitch/analysis`}
          variant="secondary"
          fullWidth
          className="mt-3"
        >
          See how your pitch reads
        </LinkButton>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* D-3a — AI processing failed                                         */
/* ------------------------------------------------------------------ */

function ProcessingFailed({ slug }: { slug: string }) {
  return (
    <Card padding="md">
      <div className="text-center">
        <div className="text-5xl">⚠️</div>
        <h2 className="mt-3 text-[20px] font-bold text-[#991b1b]">
          We couldn't process your pitch
        </h2>
        <p className="mt-1 text-[13px] text-[#6c757d]">
          Something went wrong. Here are the most common reasons:
        </p>
      </div>

      <Card tone="flagged" padding="sm" className="mt-5 shadow-none">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#92400e]">
          Issues detected
        </p>
        <ul className="mt-1 space-y-1 text-[13px] text-[#78350f]">
          <li>· Audio too quiet — try a quieter room or move closer to the mic</li>
          <li>· Face not detected for parts of the recording — keep looking at the camera</li>
        </ul>
      </Card>

      <Card tone="info" padding="sm" className="mt-3 shadow-none">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#1d4ed8]">
          A few tips for the re-record
        </p>
        <p className="mt-1 text-[13px] text-[#1d4ed8]">
          Find a quiet spot, hold the phone at face level, speak naturally — like you're meeting
          someone at the event.
        </p>
      </Card>

      <div className="mt-5 flex flex-col gap-2">
        <LinkButton to={`/e/${slug}/pitch`} fullWidth>
          ↻ Re-record pitch
        </LinkButton>
        <LinkButton to="/faq" variant="secondary" fullWidth>
          Need help?
        </LinkButton>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* D-3b — AI processing flagged for organizer review                   */
/* ------------------------------------------------------------------ */

function FlaggedForReview({
  participant,
  session,
  slug,
  videoUrl,
}: {
  participant: ParticipantRow | null | undefined;
  session: { name: string };
  slug: string;
  videoUrl: string | undefined;
}) {
  return (
    <Card padding="md">
      <div className="text-center">
        {videoUrl ? (
          <CirclePlayer
            key={videoUrl}
            src={videoUrl}
            className="mx-auto h-40 w-40 border border-[#e2e8f0]"
          />
        ) : (
          <div className="mx-auto h-40 w-40 overflow-hidden rounded-full border border-[#e2e8f0] bg-gradient-to-br from-[#e2e8f0] to-[#cbd5e1]" />
        )}
        <h2 className="mt-3 text-[18px] font-bold text-[#1a1a1a]">
          {participant?.name ?? session.name}
        </h2>
      </div>

      <Card tone="flagged" padding="sm" className="mt-5 shadow-none">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#92400e]">
          Pitch under review
        </p>
        <p className="mt-1 text-[13px] text-[#78350f]">
          Your pitch is being reviewed by the organizer. AI spotted something to double-check —
          this is automatic and usually takes under an hour.
        </p>
      </Card>

      <Card tone="info" padding="sm" className="mt-3 shadow-none">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#1d4ed8]">
          What this means
        </p>
        <p className="mt-1 text-[13px] text-[#1d4ed8]">
          You're registered for the event. Your profile won't appear in others' feeds until the
          organizer approves it — we'll email you when it's live.
        </p>
      </Card>

      <Card tone="approved" padding="sm" className="mt-3 shadow-none">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#065f46]">
          In the meantime
        </p>
        <p className="mt-1 text-[13px] text-[#065f46]">
          You can browse and like others. They won't see you back until your pitch is approved.
        </p>
      </Card>

      <div className="mt-5 flex flex-col gap-2">
        <LinkButton to={`/e/${slug}/feed`} variant="secondary" fullWidth>
          Browse the feed
        </LinkButton>
        <LinkButton to={`/e/${slug}/pitch`} variant="secondary" fullWidth>
          Edit pitch
        </LinkButton>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Rejected pitch                                                      */
/* ------------------------------------------------------------------ */

function RejectedPitch({ slug }: { slug: string }) {
  return (
    <Card tone="danger" padding="md">
      <div className="text-center">
        <div className="text-5xl">⚠️</div>
        <h2 className="mt-3 text-[20px] font-bold text-[#991b1b]">Your pitch wasn't approved</h2>
        <p className="mt-1 text-[13px] text-[#991b1b]">
          The organizer asked for a re-record — usually a quick fix.
        </p>
      </div>
      <LinkButton to={`/e/${slug}/pitch`} fullWidth className="mt-5">
        ↻ Record new pitch
      </LinkButton>
    </Card>
  );
}
